<!-- app-subdomain-creators/src/Components/CreatorChatMemberProfileComponent.vue -->

<template>
  <div
    class="w-full h-full border border-zinc-200 rounded-lg bg-white
           overflow-y-auto
           scrollbar scrollbar-thumb-zinc-100 scrollbar-track-zinc-50
           scrollbar-thumb-rounded-full scrollbar-track-rounded-full"
  >
    <!-- EMPTY STATE -->
    <div
      v-if="!member"
      class="h-full flex items-center justify-center text-sm text-zinc-400"
    >
      Select a conversation to view profile
    </div>

    <!-- PROFILE CONTENT -->
    <div v-else>
      <!-- PROFILE HEADER -->
      <div class="p-5 border-b border-zinc-100 text-center">
        <img
          :src="resolvedMemberAvatar"
          alt="Member avatar"
          class="w-24 h-24 mx-auto rounded-full object-cover mb-3"
          @error="handleMemberAvatarError"
        />

        <!-- Real name (First + Last) -->
        <h2 class="text-lg font-semibold text-zinc-900">
          {{ member.name }}
        </h2>

        <!-- Alias under real name – just display whatever is in alias -->
        <p class="text-sm text-zinc-500 mt-1">
          {{ member.alias }}
        </p>

        <!-- Handle under that -->
        <p class="text-sm text-zinc-500 mt-1">
          <!-- handle in DB already contains '@', so don't prepend another @ -->
          <span v-if="member.handle">
            {{ member.handle }}
          </span>
          <span v-else-if="member.username">
            @{{ member.username }}
          </span>
        </p>

        <!-- Subscription / Account Status -->
        <div class="mt-3 flex justify-center">
          <span
            v-if="statusLabel"
            class="text-xs font-semibold px-3 py-1 rounded-full bg-green-100 text-green-700"
          >
            {{ statusLabel }}
          </span>
        </div>
      </div>

      <!-- PROFILE DETAILS -->
      <div class="p-5 space-y-6">
        <!-- EMAIL + PLAN -->
        <div class="space-y-2 text-sm">
          <!-- EMAIL -->
          <div class="grid grid-cols-[auto,1fr] gap-4 items-start">
            <!-- Label -->
            <span class="text-zinc-500 mt-0.5">Email</span>

            <!-- Value + copy -->
            <div class="flex items-center justify-end gap-2 min-w-0">
              <!-- COPY BUTTON + TOOLTIP -->
              <button
                type="button"
                class="relative group/copy cursor-pointer shrink-0"
                @click.stop="copyEmailToClipboard"
              >
                <!-- Clipboard / Copied icon -->
                <ClipboardIcon
                  v-if="!copiedEmail"
                  class="w-5 h-5 text-zinc-400 group-hover/copy:text-zinc-700 transition"
                />
                <CheckCircleIcon
                  v-else
                  class="w-5 h-5 text-green-500 transition"
                />

                <!-- Copy tooltip (tight to icon) -->
                <span
                  class="absolute hidden group-hover/copy:block px-2 py-1 text-xs rounded-md bg-black/70 
                         text-white whitespace-nowrap z-50 -top-7 left-1/2 -translate-x-1/2 shadow-lg"
                >
                  {{ copiedEmail ? 'Copied!' : 'Copy email' }}
                </span>
              </button>

              <!-- EMAIL TEXT + FULL TOOLTIP -->
              <div
                class="relative min-w-0 max-w-[220px] sm:max-w-[260px] md:max-w-[280px] group/email"
              >
                <!-- Truncated display -->
                <span
                  class="block text-zinc-700 font-medium text-left truncate"
                >
                  {{ member.email }}
                </span>

                <!-- Pretty tooltip with full email -->
                <span
                  class="pointer-events-none absolute hidden group-hover/email:block px-2 py-1 text-xs rounded-md 
                         bg-black/70 text-white whitespace-normal z-50 -top-7 right-0 shadow-lg"
                >
                  {{ member.email }}
                </span>
              </div>
            </div>
          </div>

          <!-- PLAN -->
          <div class="flex justify-between">
            <span class="text-zinc-500">Plan</span>
            <span class="text-zinc-700 font-medium">
              {{ member.plan }}
            </span>
          </div>
        </div>

        <!-- TAGS CARD -->
        <div>
          <div class="py-2">
            <!-- Header row -->
            <div class="flex items-center justify-between mb-2">
              <h3 class="text-sm font-md text-zinc-400">
                Tags
              </h3>

              <!-- Add Tag button (icon + tooltip) -->
              <button
                type="button"
                class="relative group/addTag inline-flex items-center justify-center w-7 h-7 rounded-full 
                       text-[#2D8CFF] hover:bg-zinc-100 transition"
                @click="openAssignTagModal"
              >
                <PlusIcon class="w-5 h-5" />
                <span
                  class="absolute hidden group-hover/addTag:block px-2 py-1 text-xs rounded-md bg-black/70 
                         text-white whitespace-nowrap z-50 -top-5 -right-4 -translate-x-1/2 shadow-lg"
                >
                  Add tag
                </span>
              </button>
            </div>

<!-- Tag pills -->
<div v-if="assignedTags.length" class="flex flex-wrap gap-2">
  <div
    v-for="tag in assignedTags"
    :key="tag.userTagId || tag.id"
    class="group inline-flex items-center rounded-md bg-[#2d8bff13] px-3 py-1 text-xs
           text-[#2D8CFF] font-medium border border-[#2D8CFF] shadow-sm"
  >
    <span class="truncate max-w-[180px]">
      {{ tag.name }}
    </span>

    <button
      type="button"
      class="relative ml-2 text-[11px] text-red-500 hover:text-red-600
             opacity-0 group-hover:opacity-100
             pointer-events-none group-hover:pointer-events-auto
             transition-opacity duration-150"
      @click.stop="unassignTag(tag)"
      :disabled="isMutatingTag"
    >
      ✕
      <!-- Tooltip -->
      <span
        class="absolute hidden group-hover:block px-2 py-1 text-[11px] rounded-md bg-black/70
               text-white whitespace-nowrap z-50 -top-8 left-1/2 -translate-x-1/2 shadow-lg"
      >
        Unassign Tag
      </span>
    </button>
  </div>
</div>
            <!-- Empty state for tags -->
            <div
              v-else
              class="text-[11px] text-zinc-400 text-center w-[70%] mx-auto"
            >
              No tags assigned yet. Use the + button to add a tag.
            </div>

            <!-- Small error message for tags (assign/unassign) -->
            <p
              v-if="tagError"
              class="mt-2 text-[11px] text-red-600"
            >
              {{ tagError }}
            </p>
          </div>
        </div>


        <!-- NOTES / STICKY NOTES -->
        <div>
          <!-- Header + Add button -->
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-sm font-md text-zinc-400">
              Sticky Notes
            </h3>

            <!-- Add Note button -->
            <button
              type="button"
              class="relative group/addNote inline-flex items-center justify-center w-7 h-7 rounded-full 
                     text-[#2D8CFF] hover:bg-yellow-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
              @click="openAssignNoteModal"
              :disabled="!memberId"
            >
              <PlusIcon class="w-5 h-5" />
              <span
                class="absolute hidden group-hover/addNote:block px-2 py-1 text-xs rounded-md bg-black/70 
                       text-white whitespace-nowrap z-50 -top-5 -right-4 -translate-x-1/2 shadow-lg"
              >
                Add note
              </span>
            </button>
          </div>

          <!-- Notes grid -->
          <div
            v-if="memberNotes.length"
            class="grid grid-cols-1 sm:grid-cols-2 gap-3 chat-scroll max-h-48 overflow-y-auto scrollbar scrollbar-thumb-zinc-200 scrollbar-track-zinc-100 dark:scrollbar-track-zinc-500 scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-hover:scrollbar-track-zinc-300 px-2 py-2"
          >
            <div
              v-for="note in memberNotes"
              :key="note.id"
              class="group/note relative w-full rounded-md shadow-sm px-1 py-2 text-xs text-zinc-800
                     border border-black/5"
              :style="{ backgroundColor: noteColorFor(note.id) }"
            >
              <!-- Note text -->
              <p class="whitespace-pre-wrap break-words text-zinc-900 leading-snug pr-8">
                {{ note.text }}
              </p>

              <!-- Bottom-right controls (only on this note hover) -->
              <div
                class="absolute bottom-1 right-1 flex items-center gap-1 z-10
                       opacity-0 group-hover/note:opacity-100 transition-opacity duration-150"
              >
                <!-- Edit -->
                <button
                  type="button"
                  class="relative group/editNote inline-flex items-center justify-center rounded-full p-1
                         text-zinc-600 hover:text-zinc-900 hover:bg-black/5 transition disabled:opacity-40"
                  @click.stop="openEditNoteModal(note)"
                  :disabled="isMutatingNote"
                >
                  <PencilSquareIcon class="w-4 h-4" />
                  <span
                    class="absolute hidden group-hover/editNote:block px-2 py-1 text-[11px] rounded-md bg-black/70 
                           text-white whitespace-nowrap z-50 -top-6 right-6 translate-x-1/2 shadow-lg"
                  >
                    Edit note
                  </span>
                </button>

                <!-- Delete -->
                <button
                  type="button"
                  class="relative group/deleteNote inline-flex items-center justify-center rounded-full p-1
                         text-red-400 hover:text-red-600 hover:bg-red-50 transition disabled:opacity-40"
                  @click.stop="deleteNote(note)"
                  :disabled="isMutatingNote"
                >
                  <TrashIcon class="w-4 h-4" />
                  <span
                    class="absolute hidden group-hover/deleteNote:block px-2 py-1 text-[11px] rounded-md bg-black/70 
                           text-white whitespace-nowrap z-50 -top-6 right-10 translate-x-1/2 shadow-lg"
                  >
                    Delete note
                  </span>
                </button>
              </div>
            </div>
          </div>

          <!-- Empty state -->
          <div
            v-else
            class="text-[11px] text-zinc-400 text-center w-[70%] mx-auto"
          >
            No sticky notes yet. Use the + button to add one.
          </div>

          <!-- Error -->
          <p
            v-if="notesError"
            class="mt-2 text-[11px] text-red-600"
          >
            {{ notesError }}
          </p>
        </div>





      </div>
    </div>

    <!-- Assign Tag Modal -->
    <CreatorAssignMemberTagModal
      :is-open="showAssignTagModal"
      :available-tags="allTags"
      :member-name="member?.name || ''"
      @close="showAssignTagModal = false"
      @apply="handleAssignTagApply"
    />

    <!-- Member Notes Modal -->
    <CreatorAssignMemberNoteModal
      :is-open="showAssignNoteModal"
      :member-name="member?.name || ''"
      :mode="noteModalMode"
      :initial-note-text="noteModalInitialText"
      :note-id="noteModalNoteId"
      @close="showAssignNoteModal = false"
      @save="handleNoteModalSave"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

import { ClipboardIcon, PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { CheckCircleIcon } from '@heroicons/vue/24/solid'

import CreatorAssignMemberTagModal from '@/Components/CreatorAssignMemberTagModal.vue'
import CreatorAssignMemberNoteModal from '@/Components/CreatorAssignMemberNoteModal.vue'
import defaultAvatar from '@/assets/media/images/default-avatar.png'

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL

const props = defineProps({
  member: {
    type: Object,
    default: null,
  },
})

// Normalized member id for API calls (supports multiple shapes)
const memberId = computed(() => {
  const m = props.member
  if (!m) return null

  return (
    m.userId ??   // common mapped field
    m.id ??       // fallback
    m.memberId ?? // alternate naming
    m.user_id ??  // snake_case fallback
    null
  )
})

const resolvedMemberAvatar = computed(() => {
  const raw = props.member?.avatar
  if (typeof raw !== 'string') return defaultAvatar
  const trimmed = raw.trim()
  return trimmed ? trimmed : defaultAvatar
})

function handleMemberAvatarError(event) {
  const img = event?.target
  if (!img) return
  img.src = defaultAvatar
}


// Pastel sticky-note palette
const STICKY_NOTE_COLORS = [
  '#FEF9C3', // amber-100
  '#FFEDD5', // orange-100
  '#E0F2FE', // sky-100
  '#EDE9FE', // violet-100
  '#DCFCE7', // green-100
  '#FCE7F3', // pink-100
]

/* ---------------------- STATUS LABEL ---------------------- */
const statusLabel = computed(() => {
  if (!props.member) return ''

  const raw =
    props.member.memberStatus ?? // from backend map
    props.member.member_status ?? // just in case
    ''

  if (!raw) return ''

  const s = String(raw)
  return s.charAt(0).toUpperCase() + s.slice(1) // 'active' -> 'Active'
})

/* ---------------------- EMAIL COPY ------------------------ */
const copiedEmail = ref(false)

const copyEmailToClipboard = async () => {
  if (!props.member?.email) return

  try {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(props.member.email)
    } else {
      const el = document.createElement('textarea')
      el.value = props.member.email
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
    }

    copiedEmail.value = true
    setTimeout(() => {
      copiedEmail.value = false
    }, 1500)
  } catch (err) {
    console.error('[CreatorChatMemberProfile] Failed to copy email', err)
  }
}

/* ---------------------- TAG STATE ------------------------- */

const assignedTags = ref([]) // [{ userTagId, tagId, name }]
const allTags = ref([])      // creator's tag definitions [{ id, name }]
const isMutatingTag = ref(false)
const tagError = ref('')

const showAssignTagModal = ref(false)

/* ---------------------- NOTES STATE ----------------------- */

const memberNotes = ref([]) // [{ id, text, createdAt, updatedAt }]
const notesError = ref('')
const isMutatingNote = ref(false)

const showAssignNoteModal = ref(false)
const noteModalMode = ref('create')           // 'create' | 'edit'
const noteModalNoteId = ref(null)             // note id for edit
const noteModalInitialText = ref('')          // pre-fill text for edit/create

// Color mapping: noteId -> hex
const noteColorMap = ref({})

// simple in-place shuffle for palette
function shuffleArray(arr) {
  const copy = [...arr]
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy
}

/**
 * Assign colors to notes such that we don't repeat a color
 * until all colors have been used at least once.
 * Stable per component-mount.
 */
function assignColorsForNotes(notes) {
  if (!Array.isArray(notes) || !notes.length) {
    noteColorMap.value = {}
    return
  }

  const shuffled = shuffleArray(STICKY_NOTE_COLORS)
  const map = {}

  notes.forEach((note, index) => {
    const color = shuffled[index % shuffled.length]
    map[note.id] = color
  })

  noteColorMap.value = map
}

function noteColorFor(noteId) {
  return noteColorMap.value[noteId] || STICKY_NOTE_COLORS[0]
}


// Load tags whenever member changes
watch(
  () => memberId.value,
  (id) => {
    if (id) {
      refreshTagsForMember(id)
      loadNotesForMember(id)
    } else {
      assignedTags.value = []
      memberNotes.value = []
    }
  },
  { immediate: true }
)

async function refreshTagsForMember(id) {
  tagError.value = ''
  await Promise.all([loadAllTags(), loadAssignedTags(id)])
}

/**
 * Get all tag definitions for this creator
 * (no short-circuit so new tags appear immediately after creation)
 */
async function loadAllTags() {
  try {
    const res = await fetch(`${apiProxyUrl}/api/creator-tags`, {
      method: 'GET',
      credentials: 'include',
    })

    const data = await res.json().catch(() => ({}))

    if (!res.ok) {
      console.error('[CreatorChatMemberProfile] Error loading tags:', data)
      return
    }

    const rawList = Array.isArray(data.tags)
      ? data.tags
      : data.tags || data.data || []

    allTags.value = rawList
      .map((t) => ({
        id: t.id ?? t.tag_id,
        name: t.name ?? t.tag_name,
      }))
      .filter((t) => t.id && t.name)
  } catch (err) {
    console.error('[CreatorChatMemberProfile] Failed to load tags', err)
  }
}

/**
 * Load tag assignments for this member.
 * Backend returns camelCase { userTagId, tagId, name, ... }.
 */
async function loadAssignedTags(id) {
  try {
    const url = new URL(
      `${apiProxyUrl}/api/creator-tags/assigned`,
      window.location.origin
    )
    url.searchParams.set('userId', String(id))
    url.searchParams.set('userType', 'member')

    const res = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
    })

    const data = await res.json().catch(() => ({}))

    if (!res.ok) {
      console.error('[CreatorChatMemberProfile] Error loading assigned tags:', data)
      return
    }

    const arr = Array.isArray(data.assignments)
      ? data.assignments
      : data.assignments || data.data || []

    assignedTags.value = arr
      .map((a) => ({
        // ✅ support both camelCase (backend) and snake_case (if ever returns that)
        userTagId: a.userTagId ?? a.user_tag_id ?? a.id,
        tagId: a.tagId ?? a.tag_id,
        name: a.name ?? a.tag_name,
      }))
      .filter((x) => x.userTagId && x.name)
  } catch (err) {
    console.error('[CreatorChatMemberProfile] Failed to load assigned tags', err)
  }
}

function openAssignTagModal() {
  tagError.value = ''
  showAssignTagModal.value = true
}

/**
 * Handle Apply from modal
 * payload:
 *   { mode: 'existing', tagId }
 *   or
 *   { mode: 'new', tagName }
 */
async function handleAssignTagApply(payload) {
  const id = memberId.value
  if (!id) return

  tagError.value = ''
  isMutatingTag.value = true

  try {
    const body = {
      userId: id,
      userType: 'member',
    }

    if (payload.mode === 'existing') {
      body.tagId = payload.tagId
    } else if (payload.mode === 'new') {
      body.tagName = payload.tagName
    }

    const res = await fetch(`${apiProxyUrl}/api/creator-tags/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    })

    const data = await res.json().catch(() => ({}))

    if (!res.ok || data.error) {
      console.error('[CreatorChatMemberProfile] Error assigning tag:', data)
      tagError.value = data.message || data.error || 'Could not assign tag.'
      return
    }

    // Reload definitions + assignments so:
    // - the new tag shows in allTags (when created)
    // - pills show the new assignment
    await Promise.all([loadAllTags(), loadAssignedTags(id)])

    showAssignTagModal.value = false
  } catch (err) {
    console.error('[CreatorChatMemberProfile] Failed to assign tag', err)
    tagError.value = 'Error assigning tag. Please try again.'
  } finally {
    isMutatingTag.value = false
  }
}

async function unassignTag(tag) {
  if (!tag || !tag.userTagId) return

  tagError.value = ''
  isMutatingTag.value = true

  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-tags/assign/${encodeURIComponent(tag.userTagId)}`,
      {
        method: 'DELETE',
        credentials: 'include',
      }
    )

    const data = await res.json().catch(() => ({}))

    if (!res.ok || data.error) {
      console.error('[CreatorChatMemberProfile] Error removing tag:', data)
      tagError.value = data.message || data.error || 'Could not remove tag.'
      return
    }

    assignedTags.value = assignedTags.value.filter(
      (t) => t.userTagId !== tag.userTagId
    )
  } catch (err) {
    console.error('[CreatorChatMemberProfile] Failed to remove tag', err)
    tagError.value = 'Error removing tag. Please try again.'
  } finally {
    isMutatingTag.value = false
  }
}

/**
 * Load all notes for this member via BFF:
 * GET /api/creator-member-notes?memberId=...
 */
async function loadNotesForMember(id) {
  notesError.value = ''
  try {
    const url = new URL(
      `${apiProxyUrl}/api/creator-member-notes`,
      window.location.origin
    )
    url.searchParams.set('memberId', String(id))

    const res = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
    })

    const data = await res.json().catch(() => ({}))

    if (!res.ok || data.error) {
      console.error('[CreatorChatMemberProfile] Error loading notes:', data)
      notesError.value = data.message || data.error || 'Could not load notes.'
      memberNotes.value = []
      return
    }

    const arr = Array.isArray(data.notes)
      ? data.notes
      : data.notes || data.data || []

    memberNotes.value = arr
      .map((n) => ({
        id: n.id ?? n.noteId ?? n.note_id,
        memberId: n.memberId ?? n.member_id,
        creatorId: n.creatorId ?? n.creator_id,
        text: n.noteText ?? n.note_text ?? '',
        createdAt: n.createdAt ?? n.created_at ?? null,
        updatedAt: n.updatedAt ?? n.updated_at ?? null,
      }))
      .filter((n) => n.id && n.text)

      // 🎨 assign sticky-note colors
    assignColorsForNotes(memberNotes.value)

  } catch (err) {
    console.error('[CreatorChatMemberProfile] Failed to load notes', err)
    notesError.value = 'Error loading notes. Please try again.'
    memberNotes.value = []
  }
}

/* --------- Open modals for create / edit note --------- */

function openAssignNoteModal() {
  if (!memberId.value) return
  notesError.value = ''
  noteModalMode.value = 'create'
  noteModalNoteId.value = null
  noteModalInitialText.value = ''
  showAssignNoteModal.value = true
}

function openEditNoteModal(note) {
  if (!note || !note.id) return
  notesError.value = ''
  noteModalMode.value = 'edit'
  noteModalNoteId.value = note.id
  noteModalInitialText.value = note.text || ''
  showAssignNoteModal.value = true
}

/**
 * Handle save from modal:
 * payload: { mode: 'create' | 'edit', noteId?, noteText }
 */
async function handleNoteModalSave(payload) {
  const id = memberId.value
  if (!id) return

  const trimmed = (payload?.noteText || '').trim()
  if (!trimmed) return

  notesError.value = ''
  isMutatingNote.value = true

  try {
    if (payload.mode === 'edit' && payload.noteId) {
      // UPDATE existing note
      const res = await fetch(
        `${apiProxyUrl}/api/creator-member-notes/${encodeURIComponent(payload.noteId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ noteText: trimmed }),
        }
      )

      const data = await res.json().catch(() => ({}))

      if (!res.ok || data.error) {
        console.error('[CreatorChatMemberProfile] Error updating note:', data)
        notesError.value = data.message || data.error || 'Could not update note.'
        return
      }
    } else {
      // CREATE new note
      const res = await fetch(`${apiProxyUrl}/api/creator-member-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          memberId: id,
          noteText: trimmed,
        }),
      })

      const data = await res.json().catch(() => ({}))

      if (!res.ok || data.error) {
        console.error('[CreatorChatMemberProfile] Error creating note:', data)
        notesError.value = data.message || data.error || 'Could not create note.'
        return
      }
    }

    // Reload notes to reflect change
    await loadNotesForMember(id)
    showAssignNoteModal.value = false
  } catch (err) {
    console.error('[CreatorChatMemberProfile] Failed to save note', err)
    notesError.value = 'Error saving note. Please try again.'
  } finally {
    isMutatingNote.value = false
  }
}

/**
 * Delete a note (no confirm dialog)
 */
async function deleteNote(note) {
  if (!note || !note.id) return

  const id = memberId.value
  if (!id) return

  notesError.value = ''
  isMutatingNote.value = true

  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-member-notes/${encodeURIComponent(note.id)}`,
      {
        method: 'DELETE',
        credentials: 'include',
      }
    )

    const data = await res.json().catch(() => ({}))

    if (!res.ok || data.error) {
      console.error('[CreatorChatMemberProfile] Error deleting note:', data)
      notesError.value = data.message || data.error || 'Could not delete note.'
      return
    }

    // Remove from local list
    memberNotes.value = memberNotes.value.filter((n) => n.id !== note.id)

    // Reassign colors for remaining notes
    assignColorsForNotes(memberNotes.value)
  } catch (err) {
    console.error('[CreatorChatMemberProfile] Failed to delete note', err)
    notesError.value = 'Error deleting note. Please try again.'
  } finally {
    isMutatingNote.value = false
  }
}


</script>




<style scoped>
/* Tailwind-only */
</style>
